import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = { key: 2 }

import { reactive, ref, computed, PropType, onMounted } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import PolicySelect from '@/views/dashboard/_components/policy-select/policy-select.vue'
import Keywords from '@/views/dashboard/data-safe/anti-external-control/components/Keywords.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import {
  SensitiveService,
  GetSensitiveWordsSensitiveFilesResponseDataRow,
  SensitiveFileSendSendFileRecordRequestParams,
} from '@/views/dashboard/data-safe/anti-external-control/service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  FormSize,
  FormType,
  ObjectFormItem,
  DateType,
  ActionType,
  ActionTypeValuesByKeys,
} from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'
import dayjs from 'dayjs'
import { useParseTime } from '@/hooks'
import { formatTimeStamp } from '@/utils/time'
import { bytesToSize, highLightKeywords } from '@/utils/tool'
import { PolicyType } from '@/views/dashboard/_enum/policy-type'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { useRoute } from 'vue-router'
import { SensitiveFileSendSendFileRecordRow } from '@/views/dashboard/data-safe/anti-external-control/service'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { ControlResultState } from '../../index'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import { useI18n } from 'vue-i18n'

type ExcludeRequestParams = Exclude<keyof SensitiveFileSendSendFileRecordRequestParams, 'limit' | 'page' | 'date'>


export default /*#__PURE__*/_defineComponent({
  __name: 'outbound-control-log-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  hasPermission: {
    type: Boolean,
    default: true
  },
  /** 全量侧栏：与统计/排行点击一致时不默认时间 */
  initialDateEmpty: {
    type: Boolean,
    default: false,
  },
  /** 全量侧栏：与排行图 TimeRange 一致的起止时间（Unix 秒） */
  initialTimeRangeUnix: {
    type: Array as unknown as PropType<[number, number] | undefined>,
    default: undefined,
  },
  /** 全量侧栏：预填搜索关键字（如计算机名） */
  initialKeyword: {
    type: String,
    default: undefined,
  },
  /** 全量侧栏：预填分组 id；不传则 -1 */
  initialGroupId: {
    type: String,
    default: undefined,
  },
  /** 全量侧栏：预填策略 id（优先于路由 policy_id，用于策略列表「触发次数」打开日志） */
  initialStrategyId: {
    type: String,
    default: undefined,
  },
},
  emits: ['viewAll'],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n()

const props = __props

const emits = __emit

const injector = useReflectiveInjector([SensitiveService])
const sensitiveService = injector.get(SensitiveService)
const dataApi = sensitiveService.getSensitiveWordsFileOutgoingLog.bind(sensitiveService)

const route = useRoute()
const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const total = computed(() => tableRef.value?.total || 0)

function resolveInitialStrategyId (): string | undefined {
  if (props.initialStrategyId != null && String(props.initialStrategyId).trim() !== '') {
    return String(props.initialStrategyId).trim()
  }
  const pid = route.query.policy_id
  if (typeof pid === 'string' && pid.trim()) return pid.trim()
  if (Array.isArray(pid) && pid[0]) return String(pid[0]).trim()
  return undefined
}

function resolveFullInitialTime():
  | { dateRange: [string, string] | undefined; start: number | undefined; end: number | undefined }
  | null {
  if (props.viewMode !== TableViewMode.full) return null
  if (props.initialDateEmpty) {
    return { dateRange: undefined, start: undefined, end: undefined }
  }
  const unix = props.initialTimeRangeUnix
  if (unix && unix.length === 2 && unix[0] != null && unix[1] != null) {
    const [a, b] = unix
    return {
      dateRange: [dayjs.unix(a).format('YYYY-MM-DD'), dayjs.unix(b).format('YYYY-MM-DD')] as [string, string],
      start: a,
      end: b,
    }
  }
  const range = [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
  const [s, e] = useParseTime(range)
  return { dateRange: range, start: s, end: e }
}

const fullInitial = resolveFullInitialTime()
const initialDateRange = fullInitial != null ? fullInitial.dateRange : undefined
const [initialStartTime, initialEndTime] =
  fullInitial != null
    ? [fullInitial.start, fullInitial.end]
    : useParseTime(initialDateRange || [])

const baselineKeyword =
  props.initialKeyword !== undefined && props.initialKeyword !== null ? props.initialKeyword : ''
const baselineGroupId =
  props.initialGroupId !== undefined && props.initialGroupId !== null ? props.initialGroupId : '-1'

const queryParams = reactive<Partial<SensitiveFileSendSendFileRecordRequestParams>>({
  strategy_id: resolveInitialStrategyId(),
  dateRange: initialDateRange,
  start_time: initialStartTime,
  end_time: initialEndTime,
  group_id: baselineGroupId,
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  keyword: baselineKeyword || undefined,
})

const searchKeyword = ref(baselineKeyword)

const filterFormColumns = computed<
  ObjectFormItem<SensitiveFileSendSendFileRecordRequestParams, ExcludeRequestParams>
>(() => {
  void locale.value
  return {
    strategy_id: {
      colOpt: { style:'flex: 0 0 130px' },
      slot: true,
      label: t('outboundControlLog.triggerPolicy'),
      outSearchFiledFlag: true,
      hiddenAllLabel: true,
      events: {
        change: handleQuery,
      },
    },
    group_id: {
      colOpt: { style:'flex: 0 0 130px' },
      slot: true,
      label: t('outboundControlLog.group'),
      outSearchFiledFlag: true,
      hiddenAllLabel: true,
      type: FormType.Select,
      events: {
        change:handleQuery
      },
    },
    dateRange: {
      colOpt: { style:'flex: 0 0 260px' },
      label: t('outboundControlLog.timeRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      events: {
        change: (val) => {
            const [start, end] = useParseTime(val)
            queryParams.start_time = start
            queryParams.end_time = end
            handleQuery()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('remoteAuth.startPlaceholder'),
        'end-placeholder': t('remoteAuth.endPlaceholder'),
        clearable: true,
      },
    },
    control_result: {
      colOpt: { style:'flex: 0 0 130px' },
      label: t('outboundControlLog.sendStatus'),
      outSearchFiledFlag: true,
      hiddenAllLabel: true,
      type: FormType.Select,
      selectOpt: {
        clearable: true,
      },
      events: {
        change: handleQuery
      },
      dictData: [
        { label: t('outboundControlLog.sendStatusReleased'), value: ControlResultState.Released },
        { label: t('outboundControlLog.sendStatusBlocked'), value: ControlResultState.Blocked },
      ],
    },
  }
})

const isShowRawData = ref(false)
const currentRow = ref<any>({})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: t('outboundControlLog.operation'), width: 120, align: 'left', fixed: 'right' },
    actionButtons: (row: GetSensitiveWordsSensitiveFilesResponseDataRow) => [
      {
        name: t('outboundControlLog.dataSource'),
        type: 'rawData',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding:10px 16px' : undefined,
    path: 'data',
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'id',
    selection: false,
    showTip: false,
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      {
        label: t('outboundControlLog.operationTime'),
        prop: 'apply_time',
        columnOpt:{'min-width':150},
        formatter: (_row, val) => formatTimeStamp(val, 'YYYY-MM-DD HH:mm')
      },
      {
        label: t('outboundControlLog.triggerPolicy'),
        prop: 'strategy_name',
        columnOpt: { minWidth: 120 },
        formatter: (_row, val) => val || '--',
        showOverflow: true,
      },
      ...computerInfoCols(true, true, t),
      {
        label: t('outboundControlLog.outboundMethod'),
        prop: 'soft_name',
        columnOpt:{'min-width':150},
      },
      {
        label: t('outboundControlLog.fileName'),
        prop: 'file_name',
       columnOpt:{'min-width':150},
      },
      {
        label: t('outboundControlLog.fileSize'),
        prop: 'file_size',
        columnOpt:{'min-width':150},
        formatter: (_row, value) => bytesToSize(value)
      },
      {
        label: t('outboundControlLog.hitSensitive'),
        prop: 'keywords',
        columnOpt:{'min-width':200},
      },
      {
        label: t('outboundControlLog.sendStatus'),
        prop: 'control_result',
        columnOpt: { 'min-width': 140 },
      },
      {
        label: t('outboundControlLog.sourcePath'),
        prop: 'file_path',
        columnOpt:{'min-width':200},
        showOverflow: true,
      },
      {
        label: t('outboundControlLog.executeProcess'),
        prop: 'process_path',
        showOverflow: true,
        columnOpt:{'min-width':200},
      },
    ],
  }
})

async function handleSearch(value: string) {
  searchKeyword.value = value
  queryParams.keyword = value
  await refreshList()
}

async function handleQuery() {
  queryParams.keyword = searchKeyword.value
  if (queryParams.dateRange) {
    const [start, end] = useParseTime(queryParams.dateRange)
    queryParams.start_time = start
    queryParams.end_time = end
  }
  await refreshList()
}

function handleReset() {
  // full：initialDateEmpty 清空时间；否则排行传入的 Unix 或默认近三个月；preview 清空日期
  const defaultParams = props.viewMode === TableViewMode.full
    ? props.initialDateEmpty
      ? { dateRange: undefined, start_time: undefined, end_time: undefined }
      : (() => {
          const unix = props.initialTimeRangeUnix
          if (unix && unix.length === 2 && unix[0] != null && unix[1] != null) {
            const [a, b] = unix
            return {
              dateRange: [dayjs.unix(a).format('YYYY-MM-DD'), dayjs.unix(b).format('YYYY-MM-DD')] as [string, string],
              start_time: a,
              end_time: b,
            }
          }
          const range = [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
          const [s, e] = useParseTime(range)
          return { dateRange: range, start_time: s, end_time: e }
        })()
    : { dateRange: undefined, start_time: undefined, end_time: undefined }
  const kw =
    props.initialKeyword !== undefined && props.initialKeyword !== null ? props.initialKeyword : ''
  searchKeyword.value = kw
  queryParams.keyword = kw || undefined
  queryParams.group_id =
    props.initialGroupId !== undefined && props.initialGroupId !== null ? props.initialGroupId : '-1'
  Object.assign(queryParams, { strategy_id: resolveInitialStrategyId(), ...defaultParams })
  void refreshList()
}

async function updateGroupId(groupId: number) {
  queryParams.group_id = String(groupId)
  queryParams.page = 1
  await refreshList()
}

function handleAction({ type, rowItem }: { type: any; rowItem?: SensitiveFileSendSendFileRecordRow }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem
    isShowRawData.value = true
  }
}

function download(type: 0 | 1) {
  const params: Partial<import('@/views/dashboard/data-safe/anti-external-control/service').SensitiveWordsFileOutgoingLogRequestParams> = {
    group_id: queryParams.group_id?.toString(),
    start_time: queryParams.start_time?.toString(),
    end_time: queryParams.end_time?.toString(),
    keyword: queryParams.keyword,
  }
  sensitiveService.getSensitiveFileSendExport(type, params, t('outboundControlLog.exportFilePrefix'))
}

onMounted(async () => {
  if (!props.hasPermission) return
  await refreshList()
})

__expose({
  tableRef,
  getList: () => void refreshList(),
  download,
})

return (_ctx: any,_cache: any) => {
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_tag = _resolveComponent("el-tag")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      firstLoading: false,
      "has-permission": props.hasPermission,
      api: _unref(dataApi),
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((queryParams) = $event)),
      pageOption: {
      style: 'padding-bottom: 16px',
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        (props.viewMode !== _unref(TableViewMode).preview)
          ? (_openBlock(), _createBlock(SearchFilterBar, {
              key: 0,
              modelValue: queryParams,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
              "form-columns": filterFormColumns.value,
              disabled: !props.hasPermission,
              "show-search": true,
              "sync-search-from-field": "keyword",
              "search-placeholder": _ctx.$t('outboundControlLog.searchPlaceholder'),
              "popover-threshold": 2,
              onSearch: handleSearch,
              onQuery: handleQuery,
              onReset: handleReset
            }, {
              strategy_id: _withCtx(() => [
                _createVNode(PolicySelect, {
                  size: "small",
                  class: "cm-ui-mr10",
                  disabled: !props.hasPermission,
                  modelValue: queryParams.strategy_id,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams.strategy_id) = $event)),
                  "strategy-type": _unref(PolicyType).dlp_control_type,
                  onChange: _cache[1] || (_cache[1] = ($event: any) => (_unref(refreshList)()))
                }, null, 8, ["disabled", "modelValue", "strategy-type"])
              ]),
              group_id: _withCtx(() => [
                _createVNode(GroupTreeSelector, {
                  width: "100%",
                  disabled: !props.hasPermission,
                  "terminal-type": [_unref(TerminalType).windows, _unref(TerminalType).windowsServer],
                  onSelected: updateGroupId
                }, null, 8, ["disabled", "terminal-type"])
              ]),
              actions: _withCtx(() => [
                _createVNode(ExportDropdown, {
                  "has-permission": props.hasPermission,
                  onExport: download
                }, null, 8, ["has-permission"])
              ]),
              _: 1
            }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"]))
          : _createCommentVNode("", true)
      ]),
      file_name: _withCtx(({ value = '', row }: { value: string, row: Partial<GetSensitiveWordsSensitiveFilesResponseDataRow> }) => [
        _createVNode(_component_el_tooltip, {
          class: "box-item",
          effect: "dark",
          content: value,
          placement: "top-start"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", {
              innerHTML: _unref(highLightKeywords)(value, row.keywords + ''),
              class: "cm-ellipsis-line-one"
            }, null, 8, _hoisted_1)
          ]),
          _: 2
        }, 1032, ["content"])
      ]),
      keywords: _withCtx(({ value }) => [
        _createVNode(Keywords, {
          keywords: value,
          "max-count": 3
        }, null, 8, ["keywords"])
      ]),
      control_result: _withCtx(({ row }) => [
        (row.control_result === _unref(ControlResultState).Blocked)
          ? (_openBlock(), _createBlock(_component_el_tag, {
              key: 0,
              type: "danger",
              size: "small"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('outboundControlLog.sendStatusBlocked')), 1)
              ]),
              _: 1
            }))
          : (row.control_result === _unref(ControlResultState).Released)
            ? (_openBlock(), _createBlock(_component_el_tag, {
                key: 1,
                type: "success",
                size: "small"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('outboundControlLog.sendStatusReleased')), 1)
                ]),
                _: 1
              }))
            : (_openBlock(), _createElementBlock("span", _hoisted_2, "—"))
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "has-permission", "api", "modelValue"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})